import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Check, Loader2, Mail, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge, Button, Dialog, Input, Select } from '../ui';
import {
  RUTA_BUZONES, TIPOS_CUENTA, TIPOS_SECUNDARIO, anadirSecundario, borrarSecundario, correosDePersona, editarSecundario,
  mensajeDeError,
} from './api';
import { usePuedeGestionarCorreos } from './usePuedeGestionarCorreos';

/**
 * Ficha › Personal › Correos (30/10). De SOLO LECTURA en la ficha: el principal (el de entrar y el de
 * los avisos, que se sigue editando en «Contacto» como siempre), los secundarios y los buzones y listas
 * a los que pertenece la persona. Las licencias no salen aquí (decisión de Iban, 30/10).
 *
 * Los secundarios se gestionan en un MODAL que guarda por su cuenta, cada cambio al momento. No se
 * registra en el «Guardar» grande de la ficha: son otra tabla con su propia API, y mezclarlos haría
 * que un fallo de uno dejara a medias al otro.
 *
 * Sólo internas: los secundarios no existen para colaboradoras.
 *
 * VA DENTRO DEL <Form> DE LA FICHA, y `Dialog` no usa portal: todo botón lleva `type="button"` (el
 * `Button` del DS no lo fija, y dentro de un form sería `submit` y guardaría la ficha entera), y Enter
 * en un campo se intercepta por lo mismo.
 */

function ModalSecundarios({ personaId, datos, onClose, onCambio }) {
  const [nuevo, setNuevo] = useState({ email: '', tipo: 'alias' });
  const [editando, setEditando] = useState(null); // { id, email, tipo }
  const [aQuitar, setAQuitar] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');

  const accion = async (fn, reserva, despues) => {
    setOcupado(true);
    setError('');
    try {
      await fn();
      if (despues) despues();
      await onCambio();
    } catch (e) {
      setError(mensajeDeError(e, reserva));
    } finally {
      setOcupado(false);
    }
  };

  const anadir = () => accion(() => anadirSecundario(personaId, nuevo), 'No se pudo añadir el correo.', () => setNuevo({ email: '', tipo: 'alias' }));
  const guardarEdicion = () => accion(() => editarSecundario(personaId, editando.id, { email: editando.email, tipo: editando.tipo }),
    'No se pudo guardar el correo.', () => setEditando(null));
  const quitar = (s) => accion(() => borrarSecundario(personaId, s.id), 'No se pudo quitar el correo.', () => setAQuitar(null));

  return (
    <Dialog open size="lg" onClose={() => !ocupado && onClose()} title="Correos secundarios"
      description={`Principal: ${datos.principal}. Cada cambio se guarda al momento.`}
      footer={<Button type="button" variant="outline" onClick={onClose} disabled={ocupado}>Cerrar</Button>}>
      <div className="space-y-4">
        {datos.secundarios.length === 0 ? (
          <p className="text-sm text-slate-500">Ningún correo secundario todavía.</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {datos.secundarios.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                {editando && editando.id === s.id ? (
                  <>
                    <Input className="min-w-0 flex-1" value={editando.email} aria-label="Correo"
                      onChange={(e) => setEditando({ ...editando, email: e.target.value })}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (editando.email.trim() && !ocupado) guardarEdicion(); } }} />
                    <Select value={editando.tipo} aria-label="Tipo" onChange={(e) => setEditando({ ...editando, tipo: e.target.value })}>
                      {Object.entries(TIPOS_SECUNDARIO).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Select>
                    <Button type="button" size="sm" onClick={guardarEdicion} disabled={ocupado || !editando.email.trim()}><Check className="size-4" />Guardar</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setEditando(null); setError(''); }} disabled={ocupado}>Cancelar</Button>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 break-all text-sm text-slate-800">{s.email}</span>
                    <Badge variant="secondary">{TIPOS_SECUNDARIO[s.tipo] || s.tipo}</Badge>
                    {aQuitar === s.id ? (
                      <>
                        <span className="text-xs text-slate-600">¿Quitarlo?</span>
                        <Button type="button" size="sm" variant="danger" onClick={() => quitar(s)} disabled={ocupado}>Quitar</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => setAQuitar(null)} disabled={ocupado}>No</Button>
                      </>
                    ) : (
                      <>
                        <Button type="button" variant="ghost" size="icon" title="Editar" aria-label={`Editar ${s.email}`} disabled={ocupado}
                          onClick={() => { setEditando({ id: s.id, email: s.email, tipo: s.tipo }); setError(''); }}><Pencil className="size-4" /></Button>
                        <Button type="button" variant="ghost" size="icon" title="Quitar" aria-label={`Quitar ${s.email}`} disabled={ocupado}
                          onClick={() => setAQuitar(s.id)}><Trash2 className="size-4" /></Button>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="rounded-md border border-dashed border-slate-300 p-3">
          <div className="mb-2 text-xs font-medium text-slate-600">Añadir un correo secundario</div>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="min-w-0 flex-1" value={nuevo.email} placeholder="nombre.apellido@laehomes.com" aria-label="Nuevo correo secundario"
              onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (nuevo.email.trim() && !ocupado) anadir(); } }} />
            <Select value={nuevo.tipo} aria-label="Tipo del nuevo correo" onChange={(e) => setNuevo({ ...nuevo, tipo: e.target.value })}>
              {Object.entries(TIPOS_SECUNDARIO).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
            <Button type="button" onClick={anadir} disabled={ocupado || !nuevo.email.trim()}>
              {ocupado ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}Añadir
            </Button>
          </div>
        </div>

        {error && (
          <p role="alert" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />{error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

export default function BloqueCorreosFicha({ personaId }) {
  const puedeGestionar = usePuedeGestionarCorreos();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(false);

  const cargar = useCallback(async () => {
    setError('');
    try { setDatos(await correosDePersona(personaId)); } catch (e) {
      setError(mensajeDeError(e, 'No se pudieron cargar los correos.'));
    }
  }, [personaId]);
  useEffect(() => { if (personaId) cargar(); }, [personaId, cargar]);

  // Sólo buzones y listas: las licencias no se enseñan en la ficha.
  const grupos = datos ? datos.cuentas.filter((c) => c.tipo === 'buzon' || c.tipo === 'lista') : [];

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Mail className="size-4" />Correos</h4>
        {puedeGestionar && datos && (
          <Button type="button" size="sm" variant="outline" onClick={() => setModal(true)}><Pencil className="size-3.5" />Gestionar secundarios</Button>
        )}
      </div>

      {error ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-red-700"><AlertCircle className="size-4" />{error}
          <Button type="button" size="sm" variant="ghost" onClick={cargar}>Reintentar</Button></p>
      ) : !datos ? (
        <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="size-4 animate-spin" />Cargando…</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Direcciones</div>
            <ul className="space-y-1 text-sm">
              <li className="flex flex-wrap items-center gap-2">
                <span className="break-all text-slate-900">{datos.principal}</span>
                <Badge variant="primary">Principal</Badge>
              </li>
              {datos.secundarios.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-2">
                  <span className="break-all text-slate-700">{s.email}</span>
                  <Badge variant="secondary">{TIPOS_SECUNDARIO[s.tipo] || s.tipo}</Badge>
                </li>
              ))}
              {datos.secundarios.length === 0 && <li className="text-xs text-slate-400">Sin correos secundarios.</li>}
            </ul>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Buzones y listas</div>
            {grupos.length === 0 ? (
              <p className="text-xs text-slate-400">No está en ningún buzón ni lista.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {grupos.map((g) => (
                  <li key={g.id} className="flex flex-wrap items-center gap-2">
                    <span className="text-slate-800">{g.nombre}</span>
                    <span className="break-all text-xs text-slate-500">{g.correo}</span>
                    <Badge variant={TIPOS_CUENTA[g.tipo].badge}>{TIPOS_CUENTA[g.tipo].corto}</Badge>
                    {!g.activo && <Badge variant="secondary">Inactiva</Badge>}
                  </li>
                ))}
              </ul>
            )}
            {puedeGestionar && (
              <Link to={RUTA_BUZONES} className="mt-2 inline-block text-xs text-[hsl(var(--brand-light))] hover:underline">
                Gestionar buzones y listas
              </Link>
            )}
          </div>
        </div>
      )}

      {modal && datos && (
        <ModalSecundarios personaId={personaId} datos={datos} onClose={() => setModal(false)} onCambio={cargar} />
      )}
    </div>
  );
}
